import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

interface TenantOption {
  id: string;
  name: string;
  slug: string;
}

interface ActiveSession {
  impersonating: boolean;
  sessionId?: string;
  tenantId?: string;
  tenantName?: string;
  expiresAt?: string;
  remainingMinutes?: number;
}

interface AuditEntry {
  id: string;
  adminUserId: string;
  adminEmail: string;
  tenantId: string;
  tenantName: string;
  reason: string;
  startedAt: string;
  endedAt: string | null;
  duration: number | null;
}

export default function ImpersonationPage() {
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [search, setSearch] = useState("");
  const [selectedTenant, setSelectedTenant] = useState<TenantOption | null>(null);
  const [reason, setReason] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [activeSession, setActiveSession] = useState<ActiveSession>({ impersonating: false });
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchActiveSession = useCallback(async () => {
    try {
      const data = await apiFetch<ActiveSession>("/platform/impersonate/active");
      setActiveSession(data);
    } catch {
      // Not critical — session check can fail silently on first load
    }
  }, []);

  const fetchAuditLog = useCallback(async () => {
    try {
      const data = await apiFetch<{ sessions: AuditEntry[] }>("/platform/impersonate/audit");
      setAuditLog(data.sessions);
    } catch {
      // Audit log fetch failure is non-blocking
    }
  }, []);

  const fetchTenants = useCallback(async () => {
    try {
      const data = await apiFetch<{ tenants: TenantOption[] }>("/platform/tenants?limit=200");
      setTenants(data.tenants);
    } catch {
      // Tenant list fetch failure
    }
  }, []);

  useEffect(() => {
    fetchActiveSession();
    fetchAuditLog();
    fetchTenants();
  }, [fetchActiveSession, fetchAuditLog, fetchTenants]);

  // Refresh active session every 30s
  useEffect(() => {
    if (!activeSession.impersonating) return;
    const interval = setInterval(fetchActiveSession, 30000);
    return () => clearInterval(interval);
  }, [activeSession.impersonating, fetchActiveSession]);

  const filteredTenants = tenants.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.slug.toLowerCase().includes(search.toLowerCase()),
  );

  const handleStart = async () => {
    if (!selectedTenant) return;
    setIsStarting(true);
    setError(null);

    try {
      const data = await apiFetch<{ token: string; expiresAt: string; tenantName: string }>("/platform/impersonate/start", {
        method: "POST",
        body: JSON.stringify({ tenantId: selectedTenant.id, reason }),
      });

      // Store impersonation token for downstream use
      sessionStorage.setItem("impersonation_token", data.token);
      sessionStorage.setItem("impersonation_tenant", data.tenantName);
      sessionStorage.setItem("impersonation_expires", data.expiresAt);

      setActiveSession({
        impersonating: true,
        tenantId: selectedTenant.id,
        tenantName: data.tenantName,
        expiresAt: data.expiresAt,
        remainingMinutes: 30,
      });
      setSelectedTenant(null);
      setReason("");
      setIsConfirming(false);
      fetchAuditLog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start impersonation");
    } finally {
      setIsStarting(false);
    }
  };

  const handleStop = async () => {
    try {
      await apiFetch("/platform/impersonate/stop", { method: "POST" });
      sessionStorage.removeItem("impersonation_token");
      sessionStorage.removeItem("impersonation_tenant");
      sessionStorage.removeItem("impersonation_expires");
      setActiveSession({ impersonating: false });
      fetchAuditLog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to stop impersonation");
    }
  };

  const formatDuration = (seconds: number | null): string => {
    if (seconds === null) return "Active";
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  const formatDate = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Tenant Impersonation</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          View the platform as a specific tenant. All actions are audited. Sessions are limited to 30 minutes.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/30 px-4 py-3 text-sm text-[var(--color-destructive)]/70">
          {error}
          <button onClick={() => setError(null)} className="ml-3 text-[var(--color-destructive)]/80 hover:text-[var(--color-destructive)]/60">
            Dismiss
          </button>
        </div>
      )}

      {/* Active session warning */}
      {activeSession.impersonating && (
        <div className="rounded-md border border-[var(--color-warning)] bg-[var(--color-warning)]/40 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-lg">&#9888;</span>
              <div>
                <p className="font-medium text-[var(--color-warning)]/60">
                  Impersonating: {activeSession.tenantName}
                </p>
                <p className="text-sm text-[var(--color-warning)]/70">
                  {activeSession.remainingMinutes} minutes remaining
                </p>
              </div>
            </div>
            <button
              onClick={handleStop}
              className="rounded-md bg-[var(--color-warning)] px-4 py-1.5 text-sm font-medium text-white hover:bg-[var(--color-warning)]"
            >
              Stop Impersonation
            </button>
          </div>
        </div>
      )}

      {/* Start impersonation form */}
      {!activeSession.impersonating && (
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-6">
          <h2 className="text-lg font-semibold text-white">Start Impersonation</h2>

          <div className="mt-4 space-y-4">
            {/* Tenant selector */}
            <div className="relative">
              <label className="mb-1.5 block text-sm font-medium text-muted-foreground">
                Target Tenant
              </label>
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex w-full items-center justify-between rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white hover:border-slate-600"
              >
                <span className={selectedTenant ? "text-white" : "text-muted-foreground"}>
                  {selectedTenant ? selectedTenant.name : "Select a tenant..."}
                </span>
                <svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg">
                  <div className="p-2">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search tenants..."
                      className="w-full rounded border border-slate-600 bg-slate-700 px-2 py-1.5 text-sm text-white placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[var(--color-ring)]"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {filteredTenants.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No tenants found</div>
                    ) : (
                      filteredTenants.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => {
                            setSelectedTenant(t);
                            setIsDropdownOpen(false);
                            setSearch("");
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-muted-foreground hover:bg-slate-700"
                        >
                          <span className="font-medium text-white">{t.name}</span>
                          <span className="text-muted-foreground">{t.slug}</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Reason field */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-muted-foreground">
                Reason <span className="text-[var(--color-destructive)]/80">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why are you impersonating this tenant? (required for audit)"
                rows={2}
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>

            {/* Actions */}
            {!isConfirming ? (
              <button
                onClick={() => setIsConfirming(true)}
                disabled={!selectedTenant || reason.length < 5}
                className="rounded-md bg-[var(--color-warning)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-warning)]/50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Start Impersonation
              </button>
            ) : (
              <div className="rounded-md border border-[var(--color-warning)] bg-[var(--color-warning)]/30 p-4">
                <p className="text-sm font-medium text-[var(--color-warning)]/60">
                  Confirm: You are about to impersonate &ldquo;{selectedTenant?.name}&rdquo;
                </p>
                <p className="mt-1 text-xs text-[var(--color-warning)]/70">
                  This action will be recorded in the audit log. The session expires in 30 minutes.
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handleStart}
                    disabled={isStarting}
                    className="rounded-md bg-[var(--color-warning)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-warning)]/50 disabled:opacity-60"
                  >
                    {isStarting ? "Starting..." : "Confirm Impersonation"}
                  </button>
                  <button
                    onClick={() => setIsConfirming(false)}
                    className="rounded-md border border-[var(--color-border)] px-4 py-2 text-sm text-muted-foreground hover:bg-[var(--color-surface)]"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit log */}
      <div>
        <h2 className="text-lg font-semibold text-white">Audit Log</h2>
        <p className="mt-1 text-sm text-muted-foreground">Last 100 impersonation sessions</p>

        {auditLog.length === 0 ? (
          <div className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-8 text-center">
            <p className="text-sm text-muted-foreground">No impersonation sessions recorded yet.</p>
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-md border border-[var(--color-border)]">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-background)]">
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Admin</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Tenant</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Started</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Duration</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {auditLog.map((entry) => {
                  const isActive = !entry.endedAt && new Date(entry.startedAt).getTime() + 30 * 60 * 1000 > Date.now();
                  return (
                    <tr key={entry.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface)]/50">
                      <td className="px-4 py-3 text-sm text-muted-foreground">{entry.adminEmail}</td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-white">{entry.tenantName}</span>
                      </td>
                      <td className="max-w-xs truncate px-4 py-3 text-sm text-muted-foreground" title={entry.reason}>
                        {entry.reason}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(entry.startedAt)}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatDuration(entry.duration)}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs ${
                            isActive
                              ? "bg-[var(--color-warning)] text-[var(--color-warning)]/70"
                              : "bg-[var(--color-surface)] text-muted-foreground"
                          }`}
                        >
                          {isActive ? "Active" : "Ended"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
